import { IconName } from '@clarion-blueprint-shim';
import { Children, ReactNode, useEffect, useState } from 'react';
import { Disclosure, DisclosurePanel, Heading } from 'react-aria-components';
import { useLocation } from 'react-router';
import { ClarionButton } from '../../clarion/ClarionButton';
import ClarionPopover from '../../clarion/ClarionPopover';
import { cx } from '../../util';
import { PrefKey, usePref } from '../../util/prefs';
import { SideNavigationProvider, useSideNavigation } from './SideNavigationContext';

type NavigationGroupProps = {
  title: string;
  icon?: IconName;
  children: ReactNode;
  defaultExpanded?: boolean;
  expandedPrefKey: PrefKey;
};

export function NavigationGroup({
  title,
  icon,
  children,
  defaultExpanded = false,
  expandedPrefKey,
}: NavigationGroupProps) {
  const { isCollapsed } = useSideNavigation();
  const location = useLocation();

  const [isExpanded, setIsExpanded] = usePref(expandedPrefKey, defaultExpanded);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Check if any child NavigationItem is active
  const hasActiveChild = () => {
    const checkChild = (child: any): boolean => {
      if (!child || typeof child !== 'object') return false;
      if (child.props && child.props.to) {
        const { to } = child.props;
        return location.pathname === to || location.pathname.startsWith(to + '/');
      }
      if (child.props && child.props.children) {
        return Children.toArray(child.props.children).some((nestedChild) =>
          checkChild(nestedChild),
        );
      }
      return false;
    };

    return Children.toArray(children).some((child) => checkChild(child));
  };

  const isGroupActive = hasActiveChild();

  // Close popover when route changes
  useEffect(() => {
    setIsPopoverOpen(false);
  }, [location.pathname]);

  if (isCollapsed) {
    // In collapsed mode, show as a simple icon with tooltip that expands using ClarionPopover
    return (
      <ClarionPopover
        target={
          <ClarionButton
            variant={isGroupActive ? 'selected' : 'minimal'}
            icon={icon}
            intent={isGroupActive ? 'primary' : 'none'}
            tooltip={title}
            tooltipPlacement="right"
            className="w-full"
            size="large"
          />
        }
        title={title}
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
      >
        <div className="space-y-1 min-w-48">
          <SideNavigationProvider isCollapsed={false}>{children}</SideNavigationProvider>
        </div>
      </ClarionPopover>
    );
  }

  return (
    <div className="space-y-1">
      <Disclosure isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
        <Heading>
          <ClarionButton
            slot="trigger"
            variant={isGroupActive && !isExpanded ? 'selected' : 'minimal'}
            icon={icon}
            intent={isGroupActive && !isExpanded ? 'primary' : 'none'}
            fill
            textAlign="left"
            endIcon={isExpanded ? 'chevron-up' : 'chevron-down'}
            className={cx(
              'flex items-center justify-between w-full px-3 py-2 text-left rounded-md text-sm font-medium transition-colors duration-150 gap-3',
              'hover:bg-gray-100 dark:hover:bg-gray-700',
              'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800',
              'text-gray-700 dark:text-gray-200',
            )}
          >
            <span className="truncate">{title}</span>
          </ClarionButton>
        </Heading>

        <DisclosurePanel
          className={cx(
            'transition-all duration-500 ease-out',
            isExpanded ? 'opacity-100' : 'opacity-0',
          )}
        >
          <div className="space-y-1 ml-5 pt-1 border-l border-gray-200 dark:border-gray-600 pl-4">
            {children}
          </div>
        </DisclosurePanel>
      </Disclosure>
    </div>
  );
}
